import React from 'react';
import i18n from '../../../language/i18n';
import { normalizeLang } from '../../../utils/localeDataLoader';
import { useBusinessActivitiesQuery } from '../../../hooks/useBusinessActivitiesQuery';
import IntroduceSectionTitle from './IntroduceSectionTitle';
import {
  FiCpu,
  FiCloud,
  FiShield,
  FiActivity,
  FiLayers,
  FiGlobe,
  FiTrendingUp,
  FiBriefcase,
  FiBookOpen,
  FiHeart,
  FiBox,
  FiCheckCircle,
  FiServer,
  FiArrowRight
} from 'react-icons/fi';

const BADGE_TRANSLATIONS = {
  'tài chính': { vi: 'TÀI CHÍNH', en: 'FINANCE' },
  'viễn thông': { vi: 'VIỄN THÔNG', en: 'TELECOM' },
  'chính phủ': { vi: 'CHÍNH PHỦ', en: 'PUBLIC' },
  'y tế': { vi: 'Y TẾ', en: 'HEALTHCARE' },
  'giáo dục': { vi: 'GIÁO DỤC', en: 'EDUCATION' },
  'doanh nghiệp': { vi: 'DOANH NGHIỆP', en: 'ENTERPRISE' },
  'nông nghiệp': { vi: 'NÔNG NGHIỆP', en: 'AGRICULTURE' },
  'esm': { vi: 'ESM', en: 'ESM' },
  'erp': { vi: 'ERP', en: 'ERP' },
  'bảo mật': { vi: 'BẢO MẬT', en: 'SECURITY' },
  'dịch vụ điện tử': { vi: 'DỊCH VỤ ĐIỆN TỬ', en: 'E-SERVICES' },
  'bpo': { vi: 'BPO', en: 'BPO' },
  'cloud': { vi: 'CLOUD', en: 'CLOUD' },
  'tích hợp': { vi: 'TÍCH HỢP', en: 'INTEGRATION' },
  'hạ tầng': { vi: 'HẠ TẦNG', en: 'INFRASTRUCTURE' },
  'đầu tư': { vi: 'ĐẦU TƯ', en: 'INVESTMENT' }
};

const getBadgeText = (rawBadge, isVi) => {
  if (!rawBadge) return '';
  const key = rawBadge.trim().toLowerCase();
  const match = BADGE_TRANSLATIONS[key];
  if (match) return isVi ? match.vi : match.en;
  return rawBadge.toUpperCase();
};

const getCardIcon = (title = '', badge = '') => {
  const text = (title + ' ' + badge).toLowerCase();
  if (text.includes('ngân hàng') || text.includes('tài chính') || text.includes('finance')) return <FiTrendingUp />;
  if (text.includes('viễn thông') || text.includes('telecom')) return <FiGlobe />;
  if (text.includes('chính phủ') || text.includes('dịch vụ công') || text.includes('public')) return <FiBriefcase />;
  if (text.includes('y tế') || text.includes('pharma') || text.includes('medic') || text.includes('esm')) return <FiHeart />;
  if (text.includes('giáo dục') || text.includes('education')) return <FiBookOpen />;
  if (text.includes('doanh nghiệp') || text.includes('erp') || text.includes('enterprise')) return <FiBox />;
  if (text.includes('nông nghiệp') || text.includes('agri')) return <FiLayers />;
  if (text.includes('bảo mật') || text.includes('security')) return <FiShield />;
  if (text.includes('cloud') || text.includes('đám mây')) return <FiCloud />;
  if (text.includes('bpo')) return <FiActivity />;
  if (text.includes('hạ tầng') || text.includes('infrastr')) return <FiServer />;
  if (text.includes('tích hợp') || text.includes('integration')) return <FiCpu />;
  return <FiCheckCircle />;
};

function IntroduceBusinessActivities({ className = '' }) {
  const currentLang = normalizeLang(i18n.language);
  const isVi = currentLang === 'vi';
  const { data: categoryGroups = [] } = useBusinessActivitiesQuery(currentLang);

  if (!categoryGroups || categoryGroups.length === 0) return null;

  return (
    <section className={`introduce-overview__section introduce-overview__section--activities ${className}`.trim()}>
      <IntroduceSectionTitle>
        {isVi ? 'LĨNH VỰC KINH DOANH CHÍNH' : 'MAIN BUSINESS SECTORS'}
      </IntroduceSectionTitle>

      <div className="dribbble-business-sectors">
        {categoryGroups.map((group, idx) => {
          const count = group.activities?.length || 0;
          return (
            <div key={group.id || idx} className="dribbble-sector-group">
              {/* Premium Category Header Banner */}
              <div className="dribbble-sector-group__header">
                <div className="dribbble-sector-group__title-wrapper">
                  <span className="dribbble-sector-group__num">0{idx + 1}</span>
                  <div className="dribbble-sector-group__text-info">
                    <h3 className="dribbble-sector-group__title">{group.title}</h3>
                    {group.subtitle && (
                      <span className="dribbble-sector-group__subtitle">{group.subtitle}</span>
                    )}
                  </div>
                </div>

                <div className="dribbble-sector-group__counter">
                  <span className="dribbble-sector-group__count-number">{count}</span>
                  <span className="dribbble-sector-group__count-label">
                    {isVi ? 'Giải pháp' : 'Solutions'}
                  </span>
                </div>
              </div>

              {/* Activities Ultra Cards Grid */}
              <div className="dribbble-sector-grid">
                {(group.activities || []).map((activity) => (
                  <div key={activity.id} className="dribbble-activity-card">
                    {/* Top Row: Glowing Icon & Tag */}
                    <div className="dribbble-activity-card__top">
                      <div className="dribbble-activity-card__icon-box">
                        <div className="dribbble-activity-card__icon-glow" />
                        <div className="dribbble-activity-card__icon">
                          {getCardIcon(activity.title, activity.badge)}
                        </div>
                      </div>

                      {activity.badge && (
                        <span className="dribbble-activity-card__tag">
                          {getBadgeText(activity.badge, isVi)}
                        </span>
                      )}
                    </div>

                    {/* Card Content */}
                    <div className="dribbble-activity-card__body">
                      <h4 className="dribbble-activity-card__title">{activity.title}</h4>
                      {activity.description && (
                        <p className="dribbble-activity-card__desc">{activity.description}</p>
                      )}
                    </div>

                    {/* Card Action Link */}
                    <div className="dribbble-activity-card__footer">
                      <span className="dribbble-activity-card__action">
                        {isVi ? 'Tìm hiểu thêm' : 'Learn more'}
                        <FiArrowRight className="dribbble-activity-card__action-icon" />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default IntroduceBusinessActivities;
